import React, { useState } from "react";
import { HOME } from "@/constants/testIds";
import { SKYLINE_IMAGE } from "@/lib/site-data";
import { checkPostal } from "@/lib/api";
import { MapPin, ArrowRight, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function ServiceArea() {
  const [postal, setPostal] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!postal.trim()) {
      setError("Please enter your postal code");
      return;
    }
    setLoading(true);
    try {
      const r = await checkPostal(postal);
      setResult(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't check. Try again or call us.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section
      data-testid={HOME.serviceArea}
      id="service-area"
      className="relative bg-slate-900 overflow-hidden border-b-2 border-slate-900"
    >
      <img
        src={SKYLINE_IMAGE}
        alt="Toronto skyline at dusk"
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/50" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-sky-400 mb-4">
              <MapPin size={20} weight="bold" />
              <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase">Service Area</div>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              Do we serve your block?
            </h2>
            <p className="mt-5 text-slate-300 text-lg max-w-xl leading-relaxed">
              We cover all of Toronto plus the GTA &mdash; Etobicoke, North York, Scarborough, Mississauga, Brampton,
              Vaughan, Markham, Richmond Hill and beyond. Enter your postal code to confirm.
            </p>

            <form onSubmit={onSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
              <input
                data-testid={HOME.postalInput}
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                placeholder="e.g. M5V 2T6"
                aria-label="Postal code"
                className="flex-1 bg-white border-2 border-slate-900 px-5 py-4 font-display font-bold text-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500"
              />
              <button
                data-testid={HOME.postalSubmit}
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm uppercase tracking-[0.14em] border-2 border-sky-500 hover:border-sky-600 transition-colors disabled:opacity-60"
              >
                {loading ? "Checking…" : "Check"}
                {!loading && <ArrowRight size={18} weight="bold" />}
              </button>
            </form>

            {error && (
              <div className="mt-4 text-sm text-rose-300 font-medium">{error}</div>
            )}

            {result && (
              <div
                data-testid={HOME.postalResult}
                className={`mt-5 inline-flex items-start gap-3 px-5 py-4 border-2 ${
                  result.in_service_area
                    ? "bg-sky-500/15 border-sky-400 text-white"
                    : "bg-white border-slate-900 text-slate-900"
                }`}
              >
                {result.in_service_area ? (
                  <CheckCircle size={26} weight="fill" className="text-sky-300 shrink-0" />
                ) : (
                  <XCircle size={26} weight="fill" className="text-slate-700 shrink-0" />
                )}
                <div>
                  <div className="font-display font-bold text-base">{result.postal_code}</div>
                  <div className="text-sm opacity-90 mt-0.5">{result.message}</div>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="border-2 border-sky-400 bg-slate-900/60 backdrop-blur p-7">
              <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-400">By the numbers</div>
              <div className="mt-5 grid grid-cols-2 gap-5">
                {[
                  { n: "2 hr", l: "Avg delivery window" },
                  { n: "7 days", l: "Rental included" },
                  { n: "5★", l: "Customer rating" },
                  { n: "25+", l: "GTA neighbourhoods" },
                ].map((s) => (
                  <div key={s.l} className="border-b-2 border-sky-400/30 pb-4">
                    <div className="font-display font-black text-3xl text-white">{s.n}</div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-300 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
